import { useState, useEffect, useRef } from "react";
import { CurrentView } from "./CurrentView";
import { uid, T, mono, MONTHS_TR } from "./utils";
import { supabase } from "./supabase";
import * as XLSX from "xlsx";

const INIT_FIN = [
  { name: "Panna Sofia", color: "#4a6fa5" },
  { name: "Stratum", color: "#4a90a4" },
  { name: "Sealand", color: "#5a8a5a" },
  { name: "An Incomplete Calendar", color: "#9b7ec8" },
  { name: "Sermon to the Void", color: "#888" },
];

function parseEUR(val) {
  if (!val) return null;
  // Remove € symbol (anywhere), TRY, spaces, then handle thousand dots
  let s = String(val).replace(/€/g, "").replace(/TRY/g, "").trim();
  // If comma is decimal separator (e.g. "1.066,50"), remove dots first then replace comma
  if (s.includes(",")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    // No comma — dots might be thousand separators (e.g. "1.066")
    const parts = s.split(".");
    if (parts.length > 1 && parts[parts.length-1].length === 3) {
      s = s.replace(/\./g, ""); // thousand separator dots
    }
  }
  const n = parseFloat(s.trim());
  return isNaN(n) ? null : n;
}

function fmtEUR(n) {
  if (n == null) return "—";
  return "€" + new Intl.NumberFormat("de-DE", { minimumFractionDigits: 0 }).format(Math.round(n));
}

async function appendToSheet(googleToken, sheetId, entry, projectName, sheetTab) {
  if (!googleToken || !sheetId) return;
  try {
    const tab = encodeURIComponent(sheetTab || "Sheet1");
    const row = [entry.date, projectName, entry.type === "gelir" ? "Income" : "Expense", entry.description, Number(entry.amount)];
    await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${tab}!A:E:append?valueInputOption=USER_ENTERED`, {
      method: "POST",
      headers: { Authorization: `Bearer ${googleToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ values: [row] }),
    });
  } catch (e) { console.error("Sheets sync error:", e); }
}

/* ── Sheets Dashboard ─────────────────────────────────────────────────── */
function SheetsDashboard({ googleToken, sheetsId, sheetTab, projectName, projectColor }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Read TOTAL_FUND from sheet row 16 (index 16) column C
  const totalFundRow = data?.find(r => (r[1]||"").includes("TOTAL Fund"));
  const TOTAL_FUND = totalFundRow ? (parseEUR(totalFundRow[2]) || 41300) : 41300;

  async function load() {
    if (!googleToken || !sheetsId) return;
    setLoading(true); setError("");
    try {
      const tab = encodeURIComponent(sheetTab || "Sheet1");
      const res = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${sheetsId}/values/${tab}!A3:G22`,
        { headers: { Authorization: `Bearer ${googleToken}` } }
      );
      const json = await res.json();
      if (json.error) { setError(json.error.message); setLoading(false); return; }
      setData(json.values || []);
    } catch (e) { setError("Sheets yüklenemedi"); }
    setLoading(false);
  }

  useEffect(() => { load(); }, [sheetsId, googleToken]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!googleToken) return <div style={{ ...mono, fontSize: 10, color: T.mute, padding: "20px 0" }}>Google bağlı değil.</div>;
  if (!sheetsId) return <div style={{ ...mono, fontSize: 10, color: T.mute, padding: "20px 0" }}>Sheets ID girilmemiş.</div>;
  if (loading) return <div style={{ ...mono, color: T.mute, padding: "20px 0" }}>Yükleniyor...</div>;
  if (error) return <div style={{ ...mono, fontSize: 10, color: "#c0392b", padding: "20px 0" }}>{error}</div>;
  if (!data) return null;

  // Parse rows — A=status, B=description, C=note, D=EUR, E=TRY, F=who paid
  const rows = data.filter(r => {
    const status = (r[0] || "").trim();
    return status === "Spent" || status === "Upcoming";
  });

  const spent = rows.filter(r => r[0]?.trim() === "Spent");
  const upcoming = rows.filter(r => r[0]?.trim() === "Upcoming");

  const totalSpent = spent.reduce((s, r) => s + (parseEUR(r[3]) || 0), 0);
  const totalUpcoming = upcoming.reduce((s, r) => s + (parseEUR(r[3]) || 0), 0);
  const totalCommitted = totalSpent + totalUpcoming;
  const remaining = TOTAL_FUND - totalCommitted;

  // Category breakdown from column C (Note)
  const categories = {};
  rows.forEach(r => {
    const cat = (r[2] || "Other").trim();
    const eur = parseEUR(r[3]) || 0;
    categories[cat] = (categories[cat] || 0) + eur;
  });
  const catEntries = Object.entries(categories).sort((a, b) => b[1] - a[1]);
  const maxCat = catEntries[0]?.[1] || 1;

  const spentPct = Math.round((totalSpent / TOTAL_FUND) * 100);
  const upcomingPct = Math.round((totalUpcoming / TOTAL_FUND) * 100);
  const remainingPct = 100 - spentPct - upcomingPct;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ ...mono, fontSize: 9, color: T.mute }}>Sheets'ten · {sheetTab}</div>
        <button onClick={load} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 10px", fontSize: 10, cursor: "pointer", color: T.mute, fontFamily: "'JetBrains Mono',monospace", letterSpacing: "0.08em", textTransform: "uppercase" }}>Yenile</button>
      </div>

      {/* Big numbers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 1, marginBottom: 28, border: `1px solid ${T.border}`, borderRadius: 2, overflow: "hidden" }}>
        {[
          { l: "Toplam Fon", v: TOTAL_FUND, c: T.ink },
          { l: "Harcanan", v: totalSpent, c: "#c0392b" },
          { l: "Planlanan", v: totalUpcoming, c: "#c9843a" },
          { l: "Kalan", v: remaining, c: remaining > 0 ? "#2d7a2d" : "#c0392b" },
        ].map((s, i) => (
          <div key={s.l} style={{ padding: "18px 20px", background: "#fff", borderRight: i < 3 ? `1px solid ${T.border}` : "none" }}>
            <div style={{ ...mono, fontSize: 9, color: T.mute, marginBottom: 8 }}>{s.l.toUpperCase()}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: s.c, letterSpacing: "-0.02em" }}>{fmtEUR(s.v)}</div>
          </div>
        ))}
      </div>

      {/* Budget bar */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ ...mono, fontSize: 9, color: T.mute, marginBottom: 8 }}>Bütçe Kullanımı</div>
        <div style={{ height: 20, background: "#f0ece6", borderRadius: 2, overflow: "hidden", display: "flex" }}>
          <div title={`Harcanan: ${fmtEUR(totalSpent)}`} style={{ width: spentPct + "%", background: "#c0392b", height: "100%", transition: "width .5s", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {spentPct > 6 && <span style={{ ...mono, fontSize: 8, color: "#fff" }}>{spentPct}%</span>}
          </div>
          <div title={`Planlanan: ${fmtEUR(totalUpcoming)}`} style={{ width: upcomingPct + "%", background: "#c9843a", height: "100%", transition: "width .5s", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {upcomingPct > 6 && <span style={{ ...mono, fontSize: 8, color: "#fff" }}>{upcomingPct}%</span>}
          </div>
          <div style={{ flex: 1, background: "#e8efe8", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {remainingPct > 6 && <span style={{ ...mono, fontSize: 8, color: "#2d7a2d" }}>{remainingPct}%</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
          {[{ c: "#c0392b", l: "Harcanan" }, { c: "#c9843a", l: "Planlanan" }, { c: "#e8efe8", l: "Kalan", tc: "#2d7a2d" }].map(x => (
            <div key={x.l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, background: x.c, borderRadius: 1, flexShrink: 0, border: x.c === "#e8efe8" ? `1px solid #2d7a2d33` : "none" }} />
              <span style={{ ...mono, fontSize: 9, color: x.tc || T.mute }}>{x.l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Category breakdown */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ ...mono, fontSize: 9, color: T.mute, marginBottom: 12 }}>Kategori Dağılımı (EUR)</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {catEntries.map(([cat, val]) => (
            <div key={cat}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <span style={{ fontSize: 12, color: T.ink }}>{cat}</span>
                <span style={{ ...mono, fontSize: 10, color: T.mute }}>{fmtEUR(val)}</span>
              </div>
              <div style={{ height: 4, background: "#f0ece6", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ height: "100%", width: (val / maxCat * 100) + "%", background: projectColor, borderRadius: 2, opacity: 0.8 }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Spent vs Upcoming split */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Harcananlar", rows: spent, color: "#c0392b" },
          { label: "Planlananlar", rows: upcoming, color: "#c9843a" },
        ].map(({ label, rows: group, color }) => (
          <div key={label}>
            <div style={{ ...mono, fontSize: 9, color, marginBottom: 10 }}>{label.toUpperCase()} · {fmtEUR(group.reduce((s, r) => s + (parseEUR(r[3]) || 0), 0))}</div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 2, overflow: "hidden" }}>
              {group.length === 0
                ? <div style={{ padding: "12px", ...mono, fontSize: 9, color: T.mute }}>Yok</div>
                : group.map((r, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 60px", gap: 8, padding: "8px 10px", borderBottom: i < group.length - 1 ? `1px solid ${T.borderLight}` : "none", background: i % 2 === 0 ? "#fff" : T.bg, alignItems: "start" }}>
                      <div>
                        <div style={{ fontSize: 11, color: T.ink, lineHeight: 1.4 }}>{r[1]}</div>
                        {r[2] && <div style={{ ...mono, fontSize: 8, color: T.mute, marginTop: 2 }}>{r[2]}</div>}
                      </div>
                      <div style={{ ...mono, fontSize: 10, color, textAlign: "right", fontWeight: 600 }}>{fmtEUR(parseEUR(r[3]))}</div>
                    </div>
                  ))}
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...mono, fontSize: 9, color: T.mute, textAlign: "right" }}>
        {rows.length} kalem · Son güncelleme: az önce
      </div>
    </div>
  );
}

/* ── Main FinanceView ─────────────────────────────────────────────────── */

function PayersPanel({ payers, entries, onAdd, onRemove, onRename, fmt }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState("");

  const paidByMap = {};
  const paidToMap = {};
  payers.forEach(p => { paidByMap[p.name] = 0; paidToMap[p.name] = 0; });
  entries.forEach(e => {
    if (e.type === "gider" && e.paid_by && paidByMap[e.paid_by] !== undefined)
      paidByMap[e.paid_by] += Number(e.amount);
    if (e.paid_to && paidToMap[e.paid_to] !== undefined)
      paidToMap[e.paid_to] += Number(e.amount);
  });

  return (
    <div style={{ background:"#fff", border:`1px solid ${T.border}`, borderRadius:2, padding:"14px 16px", marginBottom:20 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div style={{ ...mono, fontSize:9, color:T.mute }}>ÖDEYENLER</div>
        {adding ? (
          <div style={{ display:"flex", gap:5 }}>
            <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="İsim" autoFocus
              onKeyDown={e=>{ if(e.key==="Enter"&&newName.trim()){ onAdd(newName); setNewName(""); setAdding(false); } if(e.key==="Escape") setAdding(false); }}
              style={{ border:`1px solid ${T.accent}`, borderRadius:2, padding:"3px 8px", fontSize:12, fontFamily:"'Inter',sans-serif", outline:"none", width:120 }}/>
            <button onClick={()=>{ if(newName.trim()){ onAdd(newName); setNewName(""); setAdding(false); } }}
              style={{ background:T.ink, color:"#fff", border:"none", borderRadius:2, padding:"3px 10px", fontSize:11, cursor:"pointer" }}>Ekle</button>
            <button onClick={()=>setAdding(false)} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:2, padding:"3px 8px", fontSize:11, cursor:"pointer", color:T.mute }}>İptal</button>
          </div>
        ) : (
          <button onClick={()=>setAdding(true)} style={{ ...mono, fontSize:9, background:"none", border:`1px solid ${T.border}`, borderRadius:2, padding:"3px 8px", cursor:"pointer", color:T.mute }}>+ Kişi Ekle</button>
        )}
      </div>
      {payers.length === 0
        ? <div style={{ ...mono, fontSize:9, color:T.mute }}>Henüz kişi eklenmemiş.</div>
        : <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {payers.map(p => {
              const paid = paidByMap[p.name] || 0;
              const received = paidToMap[p.name] || 0;
              const remaining = paid - received;
              return (
                <div key={p.id} style={{ background:T.bg, border:`1px solid ${remaining > 0 ? "#f0b8b8" : T.border}`, borderRadius:4, padding:"10px 14px", minWidth:160 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                    {editingId === p.id
                      ? <input value={editVal} onChange={e=>setEditVal(e.target.value)} autoFocus
                          onBlur={()=>{ if(editVal.trim()) onRename(p.id,editVal.trim()); setEditingId(null); }}
                          onKeyDown={e=>{ if(e.key==="Enter"&&editVal.trim()){ onRename(p.id,editVal.trim()); setEditingId(null); } if(e.key==="Escape") setEditingId(null); }}
                          style={{ fontSize:12, fontWeight:600, border:`1px solid ${T.accent}`, borderRadius:2, padding:"1px 4px", outline:"none", fontFamily:"'Inter',sans-serif", width:90 }}/>
                      : <span onDoubleClick={()=>{ setEditingId(p.id); setEditVal(p.name); }}
                          style={{ fontSize:13, fontWeight:600, color:T.ink, cursor:"text" }}>{p.name}</span>}
                    <button onClick={()=>onRemove(p.id)} style={{ background:"none", border:"none", color:T.mute, fontSize:12, cursor:"pointer", padding:0, opacity:0.4 }}
                      onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.4"}>×</button>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                    <div style={{ display:"flex", justifyContent:"space-between" }}>
                      <span style={{ ...mono, fontSize:8, color:T.mute }}>Ödedi</span>
                      <span style={{ ...mono, fontSize:9, color:"#c0392b", fontWeight:600 }}>{fmt(paid)}</span>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between" }}>
                      <span style={{ ...mono, fontSize:8, color:T.mute }}>Geri alınan</span>
                      <span style={{ ...mono, fontSize:9, color:"#2d7a2d", fontWeight:600 }}>{fmt(received)}</span>
                    </div>
                    <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:4, marginTop:2, display:"flex", justifyContent:"space-between" }}>
                      <span style={{ ...mono, fontSize:8, color:T.mute }}>Kalan borç</span>
                      <span style={{ ...mono, fontSize:10, fontWeight:700, color: remaining > 0 ? "#c0392b" : "#2d7a2d" }}>
                        {remaining > 0 ? fmt(remaining) : "✓ Ödendi"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>}
    </div>
  );
}

function InlineEdit({ value, onSave, style={} }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(()=>setVal(value),[value]);
  const commit = () => { setEditing(false); if(val!==value) onSave(val); };
  if (editing) return (
    <input value={val} onChange={e=>setVal(e.target.value)}
      onBlur={commit} onKeyDown={e=>{if(e.key==="Enter")commit();if(e.key==="Escape"){setVal(value);setEditing(false);}}}
      autoFocus
      style={{...style,background:"#fff",border:`1px solid #4a6fa5`,borderRadius:2,padding:"1px 4px",outline:"none",width:"100%",fontFamily:"'Inter',sans-serif"}}/>
  );
  return (
    <span onDoubleClick={()=>setEditing(true)} title="Düzenlemek için çift tıkla"
      style={{...style,cursor:"text",width:"100%",display:"block",fontFamily:"'Inter',sans-serif"}}>
      {value||<span style={{color:"#ccc",fontStyle:"italic"}}>—</span>}
    </span>
  );
}

export function FinanceView({ googleToken }) {
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selProject, setSelProject] = useState(null);
  const [finTab, setFinTab] = useState("projects");
  const [detailTab, setDetailTab] = useState("manual");
  const [newEntry, setNewEntry] = useState({ type: "gelir", description: "", amount: "", date: new Date().toISOString().slice(0, 10), paid_by: "", paid_to: "", original_amount: "", currency: "EUR", exchange_rate: "1" });
  const [newProjName, setNewProjName] = useState("");
  const [loading, setLoading] = useState(true);
  const [sheetInputs, setSheetInputs] = useState({});
  const [sheetTabInputs, setSheetTabInputs] = useState({});
  const [syncMsg, setSyncMsg] = useState("");
  const fileRef = useRef();

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAll() {
    setLoading(true);
    const [{ data: fp }, { data: fe }, { data: inv }] = await Promise.all([
      supabase.from("finance_projects").select("*").order("created_at"),
      supabase.from("finance_entries").select("*").order("date", { ascending: false }),
      supabase.from("invoices").select("id,name,month,year,size,uploaded_at").order("created_at", { ascending: false }),
    ]);
    if (fp && fp.length > 0) {
      setProjects(fp.map(p => ({ ...p, entries: (fe || []).filter(e => e.finance_project_id === p.id) })));
      const inputs = {}; const tabInputs = {};
      fp.forEach(p => { inputs[p.id] = p.sheets_id || ""; tabInputs[p.id] = p.sheet_tab || "Sheet1"; });
      setSheetInputs(inputs); setSheetTabInputs(tabInputs);
    } else {
      const rows = INIT_FIN.map(f => ({ id: uid(), name: f.name, color: f.color, sheets_id: "", sheet_tab: "Sheet1" }));
      await supabase.from("finance_projects").insert(rows);
      setProjects(rows.map(r => ({ ...r, entries: [] })));
      const inputs = {}; const tabInputs = {};
      rows.forEach(r => { inputs[r.id] = ""; tabInputs[r.id] = "Sheet1"; });
      setSheetInputs(inputs); setSheetTabInputs(tabInputs);
    }
    setInvoices(inv || []);
    setLoading(false);
  }

  const proj = projects.find(p => p.id === selProject);
  const fmt = n => new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2 }).format(n) + " €";

  async function addEntry() {
    if (!newEntry.description || (!newEntry.original_amount && !newEntry.amount)) return;
    const origAmt = parseFloat(newEntry.original_amount) || parseFloat(newEntry.amount) || 0;
    const rate = parseFloat(newEntry.exchange_rate) || 1;
    const eurAmount = newEntry.currency === "EUR" ? origAmt : origAmt / rate;
    const entry = { id: uid(), finance_project_id: selProject, type: newEntry.type, description: newEntry.description, amount: eurAmount, date: newEntry.date, paid_by: newEntry.paid_by || "", paid_to: newEntry.paid_to || "", original_amount: origAmt, currency: newEntry.currency, exchange_rate: rate };
    setProjects(ps => ps.map(p => p.id === selProject ? { ...p, entries: [entry, ...p.entries] } : p));
    setNewEntry(e => ({ ...e, description: "", amount: "", original_amount: "", paid_by: "", paid_to: "" }));
    await supabase.from("finance_entries").insert(entry);
  }

  async function removeEntry(projId, entryId) {
    setProjects(ps => ps.map(p => p.id === projId ? { ...p, entries: p.entries.filter(e => e.id !== entryId) } : p));
    await supabase.from("finance_entries").delete().eq("id", entryId);
  }

  async function updateEntry(projId, entryId, patch) {
    setProjects(ps => ps.map(p => p.id === projId ? { ...p, entries: p.entries.map(e => e.id === entryId ? {...e,...patch} : e) } : p));
    await supabase.from("finance_entries").update(patch).eq("id", entryId);
  }

  async function addPayer(projId, name) {
    if (!name.trim()) return;
    const p = projects.find(x => x.id === projId);
    const payers = [...(p?.payers || []), { id: uid(), name: name.trim() }];
    setProjects(ps => ps.map(x => x.id === projId ? { ...x, payers } : x));
    await supabase.from("finance_projects").update({ payers }).eq("id", projId);
  }

  async function removePayer(projId, payerId) {
    const p = projects.find(x => x.id === projId);
    const payers = (p?.payers || []).filter(x => x.id !== payerId);
    setProjects(ps => ps.map(x => x.id === projId ? { ...x, payers } : x));
    await supabase.from("finance_projects").update({ payers }).eq("id", projId);
  }

  async function renamePayer(projId, payerId, name) {
    const p = projects.find(x => x.id === projId);
    const payers = (p?.payers || []).map(x => x.id === payerId ? { ...x, name } : x);
    setProjects(ps => ps.map(x => x.id === projId ? { ...x, payers } : x));
    await supabase.from("finance_projects").update({ payers }).eq("id", projId);
  }

  async function addProject() {
    if (!newProjName.trim()) return;
    const colors = ["#e4572e","#4a90a4","#5a8a5a","#9b7ec8","#c9843a","#4a6fa5"];
    const np = { id: uid(), name: newProjName.trim(), color: colors[projects.length % colors.length], sheets_id: "", sheet_tab: "Sheet1", entries: [] };
    setProjects(ps => [...ps, np]);
    setSheetInputs(prev => ({ ...prev, [np.id]: "" }));
    setSheetTabInputs(prev => ({ ...prev, [np.id]: "Sheet1" }));
    setNewProjName("");
    await supabase.from("finance_projects").insert({ id: np.id, name: np.name, color: np.color, sheets_id: "", sheet_tab: "Sheet1" });
  }

  async function saveSheetId(projId) {
    const sheetsId = sheetInputs[projId] || "";
    const sheetTab = sheetTabInputs[projId] || "Sheet1";
    setProjects(ps => ps.map(p => p.id === projId ? { ...p, sheets_id: sheetsId, sheet_tab: sheetTab } : p));
    await supabase.from("finance_projects").update({ sheets_id: sheetsId, sheet_tab: sheetTab }).eq("id", projId);
    setSyncMsg("Sheets bağlandı ✓"); setTimeout(() => setSyncMsg(""), 3000);
  }

  async function handleInvoiceUpload(e) {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      const now = new Date();
      const inv = { id: uid(), name: file.name, month: now.getMonth(), year: now.getFullYear(), size: (file.size/1024).toFixed(1) + " KB", storage_path: ev.target.result, uploaded_at: now.toLocaleDateString("tr-TR") };
      setInvoices(i => [inv, ...i]);
      await supabase.from("invoices").insert({ id: inv.id, name: inv.name, month: inv.month, year: inv.year, size: inv.size, storage_path: inv.storage_path, uploaded_at: inv.uploaded_at });
    };
    reader.readAsDataURL(file); e.target.value = "";
  }

  async function downloadInvoice(inv) {
    const { data } = await supabase.from("invoices").select("storage_path").eq("id", inv.id).single();
    if (data?.storage_path) { const a = document.createElement("a"); a.href = data.storage_path; a.download = inv.name; a.click(); }
  }

  async function removeInvoice(id) {
    setInvoices(i => i.filter(x => x.id !== id));
    await supabase.from("invoices").delete().eq("id", id);
  }

  const totalGelir = projects.flatMap(p => p.entries).filter(e => e.type === "gelir").reduce((s,e) => s + Number(e.amount), 0);
  const totalGider = projects.flatMap(p => p.entries).filter(e => e.type === "gider").reduce((s,e) => s + Number(e.amount), 0);

  if (loading) return <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center" }}><span style={{ ...mono, color:T.mute }}>Loading</span></div>;

  return (
    <div style={{ display:"flex", flex:1, overflow:"hidden" }}>
      {/* Sidebar */}
      <div style={{ width:210, borderRight:`1px solid ${T.border}`, padding:"28px 0", flexShrink:0, background:T.bg, overflowY:"auto" }}>
        <div style={{ padding:"0 20px 8px", ...mono, fontSize:9, color:T.mute }}>View</div>
        {["current","projects","invoices"].map(t => (
          <div key={t} onClick={() => { setFinTab(t); setSelProject(null); }} style={{ padding:"9px 20px", cursor:"pointer", borderLeft: finTab===t&&!selProject?`2px solid ${T.accent}`:"2px solid transparent" }}>
            <span style={{ fontSize:13, fontWeight: finTab===t&&!selProject?500:400, color: finTab===t&&!selProject?T.ink:T.mute }}>{t === "current" ? "Current" : t === "projects" ? "Projects" : "Invoices"}</span>
          </div>
        ))}
        {finTab==="projects" && <>
          <div style={{ padding:"20px 20px 8px", ...mono, fontSize:9, color:T.mute }}>Projects</div>
          {projects.map(p => {
            const gel = p.entries.filter(e=>e.type==="gelir").reduce((s,e)=>s+Number(e.amount),0);
            const gid = p.entries.filter(e=>e.type==="gider").reduce((s,e)=>s+Number(e.amount),0);
            return (
              <div key={p.id} onClick={() => { setSelProject(p.id); setDetailTab("manual"); }} style={{ padding:"8px 20px", cursor:"pointer", borderLeft: selProject===p.id?`2px solid ${p.color}`:"2px solid transparent" }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                  <span style={{ fontSize:12, fontWeight: selProject===p.id?500:400, color: selProject===p.id?T.ink:T.mute }}>{p.name}</span>
                  {p.sheets_id && <span style={{ ...mono, fontSize:8, color:"#2d7a2d" }}>XLS</span>}
                </div>
                {p.entries.length > 0 && <div style={{ ...mono, fontSize:9, color:(gel-gid)>=0?"#2d7a2d":"#c0392b" }}>{(gel-gid)>=0?"+":""}{fmt(gel-gid)}</div>}
              </div>
            );
          })}
          <div style={{ padding:"12px 20px 0" }}>
            <div style={{ display:"flex", gap:4 }}>
              <input value={newProjName} onChange={e=>setNewProjName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addProject()} placeholder="Project name" style={{ flex:1, background:"#fff", border:`1px solid ${T.border}`, borderRadius:2, padding:"5px 8px", fontSize:11, fontFamily:"'Inter',sans-serif" }}/>
              <button onClick={addProject} style={{ background:T.ink, color:"#fff", border:"none", borderRadius:2, padding:"5px 8px", cursor:"pointer", fontSize:13 }}>+</button>
            </div>
          </div>
        </>}
      </div>

      {/* Main */}
      <div style={{ flex:1, overflowY:"auto", padding:"28px 32px" }}>

        {/* OVERVIEW */}
        {!selProject && finTab==="projects" && (
          <div>
            <div style={{ fontSize:22, fontWeight:600, letterSpacing:"-0.02em", marginBottom:28 }}>Finance</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:1, marginBottom:32, border:`1px solid ${T.border}`, borderRadius:2, overflow:"hidden" }}>
              {[{l:"Income",v:totalGelir,c:"#2d7a2d"},{l:"Expenses",v:totalGider,c:"#c0392b"},{l:"Net",v:totalGelir-totalGider,c:(totalGelir-totalGider)>=0?"#2d7a2d":"#c0392b"}].map((s,i) => (
                <div key={s.l} style={{ padding:"20px 24px", background:"#fff", borderRight:i<2?`1px solid ${T.border}`:"none" }}>
                  <div style={{ ...mono, fontSize:9, color:T.mute, marginBottom:8 }}>{s.l.toUpperCase()}</div>
                  <div style={{ fontSize:22, fontWeight:600, color:s.c, letterSpacing:"-0.02em" }}>{fmt(s.v)}</div>
                </div>
              ))}
            </div>
            <div style={{ ...mono, fontSize:9, color:T.mute, marginBottom:10 }}>By Project</div>
            <div style={{ border:`1px solid ${T.border}`, borderRadius:2, overflow:"hidden" }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 100px 100px 100px", padding:"8px 16px", background:T.bg, borderBottom:`1px solid ${T.border}` }}>
                {["Project","Income","Expenses","Net"].map(h => <span key={h} style={{ ...mono, fontSize:9, color:T.mute }}>{h}</span>)}
              </div>
              {projects.map((p,i) => {
                const gel = p.entries.filter(e=>e.type==="gelir").reduce((s,e)=>s+Number(e.amount),0);
                const gid = p.entries.filter(e=>e.type==="gider").reduce((s,e)=>s+Number(e.amount),0);
                return (
                  <div key={p.id} onClick={() => { setSelProject(p.id); setDetailTab("manual"); }} style={{ display:"grid", gridTemplateColumns:"1fr 100px 100px 100px", padding:"12px 16px", background:i%2===0?"#fff":T.bg, cursor:"pointer", borderBottom:`1px solid ${T.borderLight}`, alignItems:"center" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f0ece6"} onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"#fff":T.bg}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ width:6, height:6, borderRadius:"50%", background:p.color, flexShrink:0 }}/>
                      <span style={{ fontSize:13, fontWeight:500 }}>{p.name}</span>
                      <span style={{ ...mono, fontSize:9, color:T.mute }}>{p.entries.length}r</span>
                      {p.sheets_id && <span style={{ ...mono, fontSize:8, color:"#2d7a2d", background:"#f0fdf0", padding:"1px 4px", borderRadius:2 }}>Sheets</span>}
                    </div>
                    <span style={{ fontSize:12, color:"#2d7a2d" }}>{fmt(gel)}</span>
                    <span style={{ fontSize:12, color:"#c0392b" }}>{fmt(gid)}</span>
                    <span style={{ fontSize:12, fontWeight:600, color:(gel-gid)>=0?"#2d7a2d":"#c0392b" }}>{(gel-gid)>=0?"+":""}{fmt(gel-gid)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* PROJECT DETAIL */}
        {selProject && proj && (
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
              <button onClick={()=>setSelProject(null)} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:2, padding:"4px 10px", fontSize:11, cursor:"pointer", color:T.mute }}>← Back</button>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ width:8, height:8, borderRadius:"50%", background:proj.color }}/>
                <span style={{ fontSize:20, fontWeight:600, letterSpacing:"-0.02em" }}>{proj.name}</span>
              </div>
              {syncMsg && <span style={{ ...mono, fontSize:9, color:"#2d7a2d" }}>{syncMsg}</span>}
            </div>

            {/* Tabs: Manuel Kayıtlar | Bütçe */}
            <div style={{ display:"flex", gap:20, marginBottom:20, borderBottom:`1px solid ${T.borderLight}` }}>
              {[["manual","Manuel Kayıtlar"],["budget","Bütçe"]].map(([k,l]) => (
                <button key={k} onClick={()=>setDetailTab(k)} style={{ background:"none", border:"none", padding:"0 0 10px", fontSize:13, fontWeight:detailTab===k?500:400, color:detailTab===k?T.ink:T.mute, cursor:"pointer", borderBottom:detailTab===k?`2px solid ${T.accent}`:"2px solid transparent", marginBottom:-1 }}>{l}</button>
              ))}
            </div>

            {/* MANUAL TAB */}
            {detailTab === "manual" && (
              <div>
                {/* Export button */}
                <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
                  <button onClick={() => {
                    const rows = proj.entries.map(e => ({
                      "Tür":        e.type === "gelir" ? "Gelir" : "Gider",
                      "Açıklama":   e.description,
                      "Ödeyen":     e.paid_by || "",
                      "Ödenen":     e.paid_to || "",
                      "Tarih":      e.date,
                      "Orijinal Tutar": e.original_amount || e.amount,
                      "Para Birimi": e.currency || "EUR",
                      "Kur":        e.exchange_rate || 1,
                      "EUR Tutarı": e.amount,
                    }));
                    const ws = XLSX.utils.json_to_sheet(rows);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Manuel Kayıtlar");
                    XLSX.writeFile(wb, `${proj.name} - Manuel Kayıtlar.xlsx`);
                  }} style={{ ...mono, fontSize:9, background:"none", border:`1px solid ${T.border}`, borderRadius:2, padding:"5px 12px", cursor:"pointer", color:T.mute, display:"flex", alignItems:"center", gap:5 }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=T.ink;e.currentTarget.style.color=T.ink;}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.mute;}}>
                    ↓ Excel'e Aktar
                  </button>
                </div>
                <PayersPanel
                  payers={proj.payers || []}
                  entries={proj.entries}
                  onAdd={name => addPayer(proj.id, name)}
                  onRemove={id => removePayer(proj.id, id)}
                  onRename={(id, name) => renamePayer(proj.id, id, name)}
                  fmt={fmt}
                />
                {(() => {
                  const gel=proj.entries.filter(e=>e.type==="gelir").reduce((s,e)=>s+Number(e.amount),0);
                  const gid=proj.entries.filter(e=>e.type==="gider").reduce((s,e)=>s+Number(e.amount),0);
                  return (
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:1, marginBottom:20, border:`1px solid ${T.border}`, borderRadius:2, overflow:"hidden" }}>
                      {[{l:"Income",v:gel,c:"#2d7a2d"},{l:"Expenses",v:gid,c:"#c0392b"},{l:"Net",v:gel-gid,c:(gel-gid)>=0?"#2d7a2d":"#c0392b"}].map((s,i)=>(
                        <div key={s.l} style={{ padding:"14px 18px", background:"#fff", borderRight:i<2?`1px solid ${T.border}`:"none" }}>
                          <div style={{ ...mono, fontSize:9, color:T.mute, marginBottom:4 }}>{s.l}</div>
                          <div style={{ fontSize:18, fontWeight:600, color:s.c, letterSpacing:"-0.02em" }}>{fmt(s.v)}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <div style={{ background:"#fff", border:`1px solid ${T.border}`, borderRadius:2, padding:"16px", marginBottom:20 }}>
                  <div style={{ ...mono, fontSize:9, color:T.mute, marginBottom:12 }}>New Entry</div>
                  <div style={{ display:"grid", gridTemplateColumns:"80px 1fr 90px 90px 70px 60px 70px 90px 50px", gap:6, alignItems:"center" }}>
                    <select value={newEntry.type} onChange={e=>setNewEntry(n=>({...n,type:e.target.value}))} style={{ background:newEntry.type==="gelir"?"#f0fdf0":"#fff0f0", border:`1px solid ${newEntry.type==="gelir"?"#bbf7bb":"#fbb"}`, borderRadius:2, padding:"6px 6px", fontSize:11, fontWeight:600, color:newEntry.type==="gelir"?"#2d7a2d":"#c0392b", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                      <option value="gelir">+ Income</option>
                      <option value="gider">− Expense</option>
                    </select>
                    <input value={newEntry.description} onChange={e=>setNewEntry(n=>({...n,description:e.target.value}))} placeholder="Description" style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:2, padding:"6px 10px", fontSize:12, fontFamily:"'Inter',sans-serif" }}/>
                    <select value={newEntry.paid_by} onChange={e=>setNewEntry(n=>({...n,paid_by:e.target.value}))}
                      style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:2, padding:"6px 6px", fontSize:11, fontFamily:"'Inter',sans-serif", color:newEntry.paid_by?T.ink:T.mute }}>
                      <option value="">Ödeyen —</option>
                      {(proj.payers||[]).map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                    </select>
                    <select value={newEntry.paid_to||""} onChange={e=>setNewEntry(n=>({...n,paid_to:e.target.value}))}
                      style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:2, padding:"6px 6px", fontSize:11, fontFamily:"'Inter',sans-serif", color:newEntry.paid_to?T.ink:T.mute }}>
                      <option value="">Ödenen —</option>
                      {(proj.payers||[]).map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                    </select>
                    {/* Original amount + currency */}
                    <input value={newEntry.original_amount} onChange={e=>setNewEntry(n=>({...n,original_amount:e.target.value}))} placeholder="Tutar" type="number"
                      style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:2, padding:"6px 6px", fontSize:12, fontFamily:"'Inter',sans-serif" }}/>
                    <select value={newEntry.currency} onChange={e=>setNewEntry(n=>({...n,currency:e.target.value,exchange_rate:e.target.value==="EUR"?"1":n.exchange_rate}))}
                      style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:2, padding:"6px 4px", fontSize:11, fontFamily:"'Inter',sans-serif", fontWeight:600 }}>
                      <option value="EUR">EUR</option>
                      <option value="TRY">TRY</option>
                      <option value="USD">USD</option>
                      <option value="GBP">GBP</option>
                    </select>
                    {/* Exchange rate — only relevant if not EUR */}
                    <input value={newEntry.exchange_rate} onChange={e=>setNewEntry(n=>({...n,exchange_rate:e.target.value}))} placeholder="Kur"
                      type="number" disabled={newEntry.currency==="EUR"}
                      title="1 EUR = kaç birim?"
                      style={{ background:newEntry.currency==="EUR"?"#f0f0f0":T.bg, border:`1px solid ${T.border}`, borderRadius:2, padding:"6px 6px", fontSize:11, fontFamily:"'Inter',sans-serif", color:newEntry.currency==="EUR"?T.mute:T.ink }}/>
                    <input value={newEntry.date} onChange={e=>setNewEntry(n=>({...n,date:e.target.value}))} type="date" style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:2, padding:"6px 6px", fontSize:11, fontFamily:"'Inter',sans-serif" }}/>
                    <button onClick={addEntry} style={{ background:T.ink, color:"#fff", border:"none", borderRadius:2, padding:"6px 0", fontSize:12, cursor:"pointer", fontWeight:500 }}>Add</button>
                  </div>
                  {/* EUR preview */}
                  {newEntry.currency !== "EUR" && newEntry.original_amount && newEntry.exchange_rate && (
                    <div style={{ ...mono, fontSize:9, color:T.mute, marginTop:8 }}>
                      EUR karşılığı: <span style={{ color:T.ink, fontWeight:600 }}>€{(parseFloat(newEntry.original_amount)/parseFloat(newEntry.exchange_rate)).toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div style={{ border:`1px solid ${T.border}`, borderRadius:2, overflow:"hidden" }}>
                  {proj.entries.length===0
                    ? <div style={{ padding:"32px", textAlign:"center", ...mono, color:T.mute }}>No entries yet.</div>
                    : <>
                        <div style={{ display:"grid", gridTemplateColumns:"65px 180px 85px 85px 95px 110px 90px 22px", padding:"8px 14px", background:T.bg, borderBottom:`1px solid ${T.border}` }}>
                          {["Type","Description","Ödeyen","Ödenen","Date","Orijinal","EUR",""].map((h,i)=><span key={i} style={{ ...mono, fontSize:9, color:T.mute }}>{h}</span>)}
                        </div>
                        {proj.entries.map((e,i)=>(
                          <div key={e.id} style={{ display:"grid", gridTemplateColumns:"65px 180px 85px 85px 95px 110px 90px 22px", padding:"7px 14px", borderBottom:`1px solid ${T.borderLight}`, alignItems:"center", background:i%2===0?"#fff":T.bg }}
                            onMouseEnter={ev=>ev.currentTarget.style.background="#f0ece6"} onMouseLeave={ev=>ev.currentTarget.style.background=i%2===0?"#fff":T.bg}>
                            <select value={e.type} onChange={ev=>updateEntry(proj.id,e.id,{type:ev.target.value})}
                              style={{...mono,fontSize:9,color:e.type==="gelir"?"#2d7a2d":"#c0392b",fontWeight:600,background:"none",border:"none",cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",padding:0,outline:"none"}}>
                              <option value="gelir">INCOME</option>
                              <option value="gider">EXPENSE</option>
                            </select>
                            <InlineEdit value={e.description} onSave={v=>updateEntry(proj.id,e.id,{description:v})} style={{fontSize:12}}/>
                            <select value={e.paid_by||""} onChange={ev=>updateEntry(proj.id,e.id,{paid_by:ev.target.value})}
                              style={{...mono,fontSize:9,color:e.paid_by?T.ink:T.mute,background:"none",border:"none",cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",padding:0,outline:"none"}}>
                              <option value="">—</option>
                              {(proj.payers||[]).map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                            </select>
                            <select value={e.paid_to||""} onChange={ev=>updateEntry(proj.id,e.id,{paid_to:ev.target.value})}
                              style={{...mono,fontSize:9,color:e.paid_to?T.ink:T.mute,background:"none",border:"none",cursor:"pointer",fontFamily:"'JetBrains Mono',monospace",padding:0,outline:"none"}}>
                              <option value="">—</option>
                              {(proj.payers||[]).map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                            </select>
                            <input type="date" value={e.date} onChange={ev=>updateEntry(proj.id,e.id,{date:ev.target.value})}
                              style={{...mono,fontSize:10,color:T.mute,background:"none",border:"none",outline:"none",fontFamily:"'JetBrains Mono',monospace",padding:0}}/>
                            {/* Original amount + currency */}
                            <div style={{display:"flex",alignItems:"center",gap:3}}>
                              <InlineEdit value={String(e.original_amount||e.amount||"")} onSave={v=>updateEntry(proj.id,e.id,{original_amount:parseFloat(v)||0})} style={{fontSize:11,color:T.mute}}/>
                              <select value={e.currency||"EUR"} onChange={ev=>updateEntry(proj.id,e.id,{currency:ev.target.value})}
                                style={{...mono,fontSize:8,background:"none",border:"none",cursor:"pointer",color:T.mute,padding:0,outline:"none"}}>
                                <option value="EUR">€</option>
                                <option value="TRY">₺</option>
                                <option value="USD">$</option>
                                <option value="GBP">£</option>
                              </select>
                              {(e.currency&&e.currency!=="EUR")&&(
                                <span style={{...mono,fontSize:8,color:"#bbb",cursor:"pointer"}} title="Kur: 1 EUR = ?">
                                  <InlineEdit value={String(e.exchange_rate||1)} onSave={v=>updateEntry(proj.id,e.id,{exchange_rate:parseFloat(v)||1})} style={{fontSize:8,color:"#bbb"}}/>
                                </span>
                              )}
                            </div>
                            <InlineEdit value={String(e.amount)} onSave={v=>updateEntry(proj.id,e.id,{amount:parseFloat(v)||e.amount})} style={{fontSize:12,fontWeight:600,color:e.type==="gelir"?"#2d7a2d":"#c0392b",textAlign:"right"}}/>
                            <button onClick={()=>removeEntry(proj.id,e.id)} style={{ background:"none", border:"none", color:"#c0392b", fontSize:14, cursor:"pointer", opacity:0.3, padding:0 }} onMouseEnter={ev=>ev.currentTarget.style.opacity="1"} onMouseLeave={ev=>ev.currentTarget.style.opacity="0.3"}>×</button>
                          </div>
                        ))}
                      </>}
                </div>
              </div>
            )}

            {/* SHEETS DASHBOARD TAB */}
            {detailTab === "sheets" && (
              <SheetsDashboard
                googleToken={googleToken}
                sheetsId={proj.sheets_id}
                sheetTab={proj.sheet_tab || sheetTabInputs[proj.id]}
                projectName={proj.name}
                projectColor={proj.color}
              />
            )}
          </div>
        )}

        {/* INVOICES */}
        {finTab==="current" && <CurrentView/>}

        {finTab==="invoices" && !selProject && (
          <div>
            <div style={{ fontSize:22, fontWeight:600, letterSpacing:"-0.02em", marginBottom:24 }}>Invoices</div>
            <button onClick={()=>fileRef.current.click()} style={{ background:T.ink, color:T.bg, border:"none", borderRadius:2, padding:"8px 18px", fontSize:12, cursor:"pointer", fontWeight:500, marginBottom:28 }}>Upload PDF</button>
            <input ref={fileRef} type="file" accept=".pdf" onChange={handleInvoiceUpload} style={{ display:"none" }}/>
            {invoices.length===0
              ? <div style={{ ...mono, color:T.mute, padding:"40px 0" }}>No invoices uploaded.</div>
              : MONTHS_TR.map((month,mi) => {
                  const mi_invs = invoices.filter(i=>i.month===mi);
                  if (!mi_invs.length) return null;
                  return <div key={mi} style={{ marginBottom:28 }}>
                    <div style={{ ...mono, fontSize:9, color:T.mute, marginBottom:10, paddingBottom:8, borderBottom:`1px solid ${T.border}` }}>{month.toUpperCase()} {mi_invs[0].year}</div>
                    {mi_invs.map(inv=>(
                      <div key={inv.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${T.borderLight}` }}>
                        <div>
                          <div style={{ fontSize:13, fontWeight:500, marginBottom:2 }}>{inv.name}</div>
                          <div style={{ ...mono, fontSize:9, color:T.mute }}>{inv.size} · {inv.uploaded_at}</div>
                        </div>
                        <div style={{ display:"flex", gap:8 }}>
                          <button onClick={()=>downloadInvoice(inv)} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:2, padding:"4px 12px", fontSize:11, cursor:"pointer", color:T.ink }}>Download</button>
                          <button onClick={()=>removeInvoice(inv.id)} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:2, padding:"4px 8px", fontSize:11, cursor:"pointer", color:"#c0392b" }}>×</button>
                        </div>
                      </div>
                    ))}
                  </div>;
                })}
            {/* BUDGET TAB */}
            {detailTab === "budget" && <BudgetView projectId={proj.id} fmt={fmt} />}

          </div>
        )}
      </div>
    </div>
  );
}

// ─── Budget View ─────────────────────────────────────────────────────────────
const BUDGET_PHASES = [
  { id: "pre-production", label: "Pre-Production", color: "#4a6fa5" },
  { id: "production",     label: "Production",     color: "#e4572e" },
  { id: "post-production",label: "Post-Production", color: "#3a8a7c" },
];

function BudgetView({ projectId, fmt }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ phase:"pre-production", category:"", label:"", estimated:"", currency:"EUR", notes:"" });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    supabase.from("finance_budget").select("*").eq("finance_project_id", projectId).order("phase").then(({ data }) => {
      setItems(data || []);
      setLoading(false);
    });
  }, [projectId]);

  async function addItem() {
    if (!newItem.label.trim() || !newItem.estimated) return;
    const item = { id: uid(), finance_project_id: projectId, ...newItem, estimated: parseFloat(newItem.estimated) };
    setItems(prev => [...prev, item]);
    await supabase.from("finance_budget").insert(item);
    setNewItem({ phase:"pre-production", category:"", label:"", estimated:"", currency:"EUR", notes:"" });
    setAdding(false);
  }

  async function updateItem(id, patch) {
    setItems(prev => prev.map(x => x.id === id ? {...x,...patch} : x));
    await supabase.from("finance_budget").update(patch).eq("id", id);
  }

  async function deleteItem(id) {
    setItems(prev => prev.filter(x => x.id !== id));
    await supabase.from("finance_budget").delete().eq("id", id);
  }

  if (loading) return <div style={{ ...mono, color:T.mute, padding:"40px 0" }}>Yükleniyor…</div>;

  const totalByPhase = BUDGET_PHASES.map(p => ({
    ...p,
    total: items.filter(x => x.phase === p.id).reduce((s, x) => s + Number(x.estimated), 0)
  }));
  const grandTotal = totalByPhase.reduce((s, p) => s + p.total, 0);

  return (
    <div>
      {/* Phase summary cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:1, marginBottom:24, border:`1px solid ${T.border}`, borderRadius:2, overflow:"hidden" }}>
        {[...totalByPhase, { id:"total", label:"TOTAL", color:T.ink, total:grandTotal }].map((p,i,arr) => (
          <div key={p.id} style={{ padding:"16px 18px", background:"#fff", borderRight:i<arr.length-1?`1px solid ${T.border}`:"none" }}>
            <div style={{ ...mono, fontSize:8, color:T.mute, marginBottom:6 }}>{p.label.toUpperCase()}</div>
            <div style={{ fontSize:18, fontWeight:700, color:p.color, letterSpacing:"-0.02em" }}>{fmt(p.total)}</div>
          </div>
        ))}
      </div>

      {/* Bar chart */}
      {grandTotal > 0 && (
        <div style={{ marginBottom:24, background:"#fff", border:`1px solid ${T.border}`, borderRadius:2, padding:"16px 18px" }}>
          <div style={{ ...mono, fontSize:9, color:T.mute, marginBottom:12 }}>DAĞILIM</div>
          <div style={{ display:"flex", height:24, borderRadius:2, overflow:"hidden", gap:1 }}>
            {totalByPhase.filter(p=>p.total>0).map(p => (
              <div key={p.id} style={{ background:p.color, width:`${(p.total/grandTotal)*100}%`, display:"flex", alignItems:"center", justifyContent:"center", minWidth:40 }}>
                <span style={{ ...mono, fontSize:7, color:"#fff" }}>{Math.round((p.total/grandTotal)*100)}%</span>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:16, marginTop:8 }}>
            {totalByPhase.filter(p=>p.total>0).map(p => (
              <div key={p.id} style={{ display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ width:8, height:8, borderRadius:2, background:p.color, flexShrink:0 }}/>
                <span style={{ ...mono, fontSize:8, color:T.mute }}>{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add button */}
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
        {adding ? (
          <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
            <select value={newItem.phase} onChange={e=>setNewItem(n=>({...n,phase:e.target.value}))}
              style={{ border:`1px solid ${T.border}`, borderRadius:2, padding:"5px 8px", fontSize:11, fontFamily:"'Inter',sans-serif", background:T.bg }}>
              {BUDGET_PHASES.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <input value={newItem.category} onChange={e=>setNewItem(n=>({...n,category:e.target.value}))} placeholder="Kategori (ör: Ekipman)"
              style={{ border:`1px solid ${T.border}`, borderRadius:2, padding:"5px 8px", fontSize:12, fontFamily:"'Inter',sans-serif", width:120, background:T.bg }}/>
            <input value={newItem.label} onChange={e=>setNewItem(n=>({...n,label:e.target.value}))} placeholder="Kalem adı" autoFocus
              onKeyDown={e=>{if(e.key==="Enter")addItem();if(e.key==="Escape")setAdding(false);}}
              style={{ border:`1px solid ${T.accent}`, borderRadius:2, padding:"5px 8px", fontSize:12, fontFamily:"'Inter',sans-serif", width:160, outline:"none" }}/>
            <input value={newItem.estimated} onChange={e=>setNewItem(n=>({...n,estimated:e.target.value}))} placeholder="Tahmini" type="number"
              style={{ border:`1px solid ${T.border}`, borderRadius:2, padding:"5px 8px", fontSize:12, fontFamily:"'Inter',sans-serif", width:90, background:T.bg }}/>
            <select value={newItem.currency} onChange={e=>setNewItem(n=>({...n,currency:e.target.value}))}
              style={{ border:`1px solid ${T.border}`, borderRadius:2, padding:"5px 6px", fontSize:11, fontFamily:"'Inter',sans-serif", background:T.bg }}>
              <option value="EUR">EUR</option><option value="TRY">TRY</option><option value="USD">USD</option>
            </select>
            <button onClick={addItem} style={{ background:T.ink, color:"#fff", border:"none", borderRadius:2, padding:"5px 14px", fontSize:11, cursor:"pointer" }}>Ekle</button>
            <button onClick={()=>setAdding(false)} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:2, padding:"5px 10px", fontSize:11, cursor:"pointer", color:T.mute }}>İptal</button>
          </div>
        ) : (
          <button onClick={()=>setAdding(true)} style={{ background:T.ink, color:"#fff", border:"none", borderRadius:2, padding:"6px 16px", fontSize:11, cursor:"pointer", fontWeight:500 }}>+ Kalem Ekle</button>
        )}
      </div>

      {/* Items by phase */}
      {BUDGET_PHASES.map(phase => {
        const phaseItems = items.filter(x => x.phase === phase.id);
        return (
          <div key={phase.id} style={{ marginBottom:20 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, paddingBottom:6, borderBottom:`2px solid ${phase.color}` }}>
              <span style={{ fontSize:13, fontWeight:600, color:phase.color }}>{phase.label}</span>
              <span style={{ ...mono, fontSize:9, color:T.mute }}>{fmt(phaseItems.reduce((s,x)=>s+Number(x.estimated),0))}</span>
            </div>
            {phaseItems.length === 0
              ? <div style={{ ...mono, fontSize:9, color:T.mute, padding:"8px 0" }}>Henüz kalem yok.</div>
              : <div style={{ border:`1px solid ${T.border}`, borderRadius:2, overflow:"hidden" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"140px 1fr 100px 60px 24px", padding:"6px 14px", background:T.bg, borderBottom:`1px solid ${T.border}` }}>
                    {["Kategori","Kalem","Tahmini","Para",""].map((h,i)=><span key={i} style={{ ...mono, fontSize:8, color:T.mute }}>{h}</span>)}
                  </div>
                  {phaseItems.map((item,i) => (
                    <div key={item.id} style={{ display:"grid", gridTemplateColumns:"140px 1fr 100px 60px 24px", padding:"7px 14px", borderBottom:`1px solid ${T.borderLight}`, alignItems:"center", background:i%2===0?"#fff":T.bg }}
                      onMouseEnter={ev=>ev.currentTarget.style.background="#f0ece6"} onMouseLeave={ev=>ev.currentTarget.style.background=i%2===0?"#fff":T.bg}>
                      <InlineEdit value={item.category} onSave={v=>updateItem(item.id,{category:v})} style={{fontSize:11,color:T.mute}}/>
                      <InlineEdit value={item.label} onSave={v=>updateItem(item.id,{label:v})} style={{fontSize:12}}/>
                      <InlineEdit value={String(item.estimated)} onSave={v=>updateItem(item.id,{estimated:parseFloat(v)||0})} style={{fontSize:12,fontWeight:600,color:phase.color}}/>
                      <select value={item.currency||"EUR"} onChange={ev=>updateItem(item.id,{currency:ev.target.value})}
                        style={{...mono,fontSize:9,background:"none",border:"none",cursor:"pointer",color:T.mute,padding:0,outline:"none"}}>
                        <option value="EUR">EUR</option><option value="TRY">TRY</option><option value="USD">USD</option>
                      </select>
                      <button onClick={()=>deleteItem(item.id)} style={{ background:"none", border:"none", color:"#c0392b", fontSize:13, cursor:"pointer", opacity:0.3, padding:0 }}
                        onMouseEnter={ev=>ev.currentTarget.style.opacity="1"} onMouseLeave={ev=>ev.currentTarget.style.opacity="0.3"}>×</button>
                    </div>
                  ))}
                </div>}
          </div>
        );
      })}
    </div>
  );
}
